import { Button, Icon, InputField } from '@design-system';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDraws } from '../../../providers/DrawProvider';
import { useFlow } from '../../../providers/FlowProvider';
import { usePreAuthDrawCtx } from '../../../providers/PreAuthDrawProvider';
import { DrawByProductId } from '../../draws/types';

interface Props {
  draw: DrawByProductId;
}

type SubmissionPhase = 'IDLE' | 'START_UPDATE' | 'AWAITING_RESPONSE';

export const BidStep = ({ draw }: Props) => {
  const { t } = useTranslation('product');
  const { goToStep } = useFlow();
  const { bidValidationError, entryIntentResponse, setBid, updating } =
    usePreAuthDrawCtx();

  const { currentWinningBidByDrawId } = useDraws();
  const currentWinningBid = currentWinningBidByDrawId[draw.id];

  const [submissionPhase, setSubmissionPhase] =
    useState<SubmissionPhase>('IDLE');

  const form = useForm({
    defaultValues: {
      bid: entryIntentResponse.intent?.bid?.amount
        ? Number(entryIntentResponse.intent.bid.amount)
        : '',
    },
  });

  const getSafeNumber = (value: string | number): number => {
    if (typeof value === 'number') return value;
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const isBidAboveReservePrice =
    draw.auctionReservePrice &&
    getSafeNumber(form.watch('bid')) >= Number(draw.auctionReservePrice.amount);

  const isBidAboveWinningBid =
    !draw.promptUserAboutWinningBid ||
    (currentWinningBid &&
      getSafeNumber(form.watch('bid')) > Number(currentWinningBid.amount));

  const isValidBid = isBidAboveReservePrice && isBidAboveWinningBid;

  const handleSubmit = ({ bid }: { bid: number }) => {
    setSubmissionPhase('START_UPDATE');
    setBid(bid);
  };

  const prompt = useMemo(() => {
    const label =
      draw.allocation > 1 ||
      (draw.auctionReservePrice &&
        draw.auctionReservePrice.amount !== '0' &&
        !draw.promptUserAboutWinningBid)
        ? 'drawers.bid.prompt.rankedAuctionBidAbove'
        : 'drawers.bid.prompt.singleAuctionHighestBid';
    if (
      draw.promptUserAboutWinningBid &&
      currentWinningBid &&
      currentWinningBid.amount !== '0'
    ) {
      return t(label, {
        amount: currentWinningBid.amount ?? 0,
      });
    }

    if (draw.auctionReservePrice && draw.auctionReservePrice.amount !== '0') {
      return t(label, {
        amount: draw.auctionReservePrice?.amount ?? 0,
      });
    }

    return null;
  }, [
    draw.allocation,
    draw.promptUserAboutWinningBid,
    draw.auctionReservePrice,
    currentWinningBid,
    t,
  ]);

  useEffect(() => {
    if (submissionPhase === 'START_UPDATE' && updating) {
      setSubmissionPhase('AWAITING_RESPONSE');
    }
    if (submissionPhase === 'AWAITING_RESPONSE' && !updating) {
      setSubmissionPhase('IDLE');
      if (!bidValidationError) {
        goToStep('frame-intro');
      }
    }
  }, [updating, submissionPhase, bidValidationError, goToStep]);

  return (
    <div className="flex flex-col gap-6">
      <form
        className="flex flex-col gap-6"
        onSubmit={(ev) => void form.handleSubmit(handleSubmit)(ev)}
      >
        {prompt && <p className="text-neutral-6">{prompt}</p>}
        <InputField
          key="bid"
          {...form.register('bid', {
            required: {
              message: t('drawers.bid.input.required'),
              value: true,
            },
            min: {
              message: t('drawers.bid.input.min'),
              value: draw.auctionReservePrice?.amount ?? 0,
            },
          })}
          error={
            form.formState.errors.bid?.message ||
            bidValidationError ||
            undefined
          }
          id="bid"
          note={`${
            draw.auctionReservePrice && draw.auctionReservePrice.amount !== '0'
              ? `${t('drawers.bid.input.reservePrice', {
                  amount: draw.auctionReservePrice?.amount,
                })} `
              : ''
          }${t('drawers.bid.input.note')}`}
          prefix={<span className="font-medium text-sm">€</span>}
        />
        <Button
          disabled={!isValidBid || submissionPhase === 'AWAITING_RESPONSE'}
          fullWidth
          label={t('drawers.bid.save')}
          // loading={loading}
          type="submit"
          variant="primary"
        />
      </form>
      {draw.allocation > 1 && (
        <div className="flex flex-col gap-4">
          <h3 className="font-bold">{t('drawers.bid.help.how_it_works')}</h3>
          <div className="flex gap-3">
            <div>
              <Icon name="ic_ticket" size="sm" />
            </div>
            <div className="pt-0.5">
              <h4>{t('drawers.bid.help.place_your_bid.title')}</h4>
              <p className="text-neutral-4">
                {t('drawers.bid.help.place_your_bid.description', {
                  allocation: draw.allocation,
                })}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div>
              <Icon name="ic_receipt" size="sm" />
            </div>
            <div className="pt-0.5">
              <h4>{t('drawers.bid.help.final_price.title')}</h4>
              <p className="text-neutral-4">
                {t('drawers.bid.help.final_price.description', {
                  allocation: draw.allocation,
                })}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div>
              <Icon name="ic_check_circle" size="sm" />
            </div>
            <div className="pt-0.5">
              <h4>
                {t('drawers.bid.help.top_bidders.title', {
                  allocation: draw.allocation,
                })}
              </h4>
              <p className="text-neutral-4">
                {t('drawers.bid.help.top_bidders.description')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
